import { api } from "@/lib/api";

/**
 * Convert a base64 URL-safe string to a Uint8Array (required by PushManager).
 */
function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
    return reg;
  } catch {
    return null;
  }
}

export function pushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getPushPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

/**
 * Request permission and subscribe this browser to Web Push for the signed-in school user.
 * Returns { ok: boolean, reason?: string }.
 */
export async function enablePushForSchool() {
  return enablePushForRole("school");
}

export async function enablePushForAdmin() {
  return enablePushForRole("admin");
}

async function enablePushForRole(role) {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: permission };

  const reg = await navigator.serviceWorker.ready;
  const keyResp = await api.get("/push/vapid-public-key");
  const key = keyResp.data?.public_key;
  if (!key) return { ok: false, reason: "no-vapid" };

  // Reuse an existing subscription if we already have one for this key, else subscribe.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(key),
    });
  }

  const url = role === "admin" ? "/admin/push/subscribe" : "/school/push/subscribe";
  const device = {
    ua: navigator.userAgent,
    origin: window.location.origin,
    standalone: window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true,
  };
  await api.post(url, { subscription: sub.toJSON(), device });
  return { ok: true };
}

export async function disablePushForSchool() {
  return disablePushForRole("school");
}

export async function disablePushForAdmin() {
  return disablePushForRole("admin");
}

async function disablePushForRole(role) {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const url = role === "admin" ? "/admin/push/unsubscribe" : "/school/push/unsubscribe";
  try { await api.post(url, { endpoint: sub.endpoint }); } catch {}
  try { await sub.unsubscribe(); } catch {}
}
